import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  Divider,
  Chip,
  Grid,
  CircularProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  Person as PersonIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { Project } from '../../types';
import { ProjectStatusBadge } from '../Common/ProjectStatusBadge';

interface User {
  id: number;
  username: string;
  full_name: string;
  role: 'Admin' | 'Gerente';
  is_active: boolean;
}

interface ProjectAssignmentProject {
  project_name: string;
  active?: boolean; // Add active status for project assignments
}

interface UserProject {
  project_name: string;
  active?: boolean; // Add active status for user projects
}

export function ProjectAssignment() {
  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [userProjects, setUserProjects] = useState<UserProject[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load users and projects on component mount
  useEffect(() => {
    loadUsers();
    loadProjects();
  }, []);

  // Load user projects when user is selected
  useEffect(() => {
    if (selectedUserId) {
      loadUserProjects(selectedUserId as number);
    } else {
      setUserProjects([]);
      setSelectedProjects(new Set());
    }
  }, [selectedUserId]);

  const loadUsers = async () => {
    try {
      const response = await fetch('/api/users');
      if (!response.ok) {
        throw new Error('Error al cargar usuarios');
      }
      const data = await response.json();
      // Filter only Gerente users for project assignment
      const gerenteUsers = data.filter((user: User) => user.role === 'Gerente' && user.is_active);
      setUsers(gerenteUsers);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    }
  };

  const loadProjects = async () => {
    try {
      // Use the enhanced configuracion_proyectos endpoint that includes active status
      const response = await fetch('/api/v1/configuracion_proyectos');
      if (!response.ok) {
        throw new Error('Error al cargar proyectos');
      }
      const data = await response.json();

      // Parse the proyectos JSON string from the API response
      const projectsArray = JSON.parse(data.proyectos);

      // Transform the data to match our Project interface
      const transformedProjects: Project[] = projectsArray.map((item: any) => ({
        'Codigo Centro Costos': item['Codigo Centro Costos'],
        'Nombre Proyecto': item['Nombre Proyecto'],
        'Tipo Centro Costos': item['Tipo Centro Costos'],
        'Ultima Actualizacion Sistema': item['Ultima Actualizacion Sistema'],
        'Ultima Actualizacion Usuario': item['Ultima Actualizacion Usuario'],
        active: item.active !== undefined ? item.active : true // Default to true for backward compatibility
      }));

      setProjects(transformedProjects);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    }
  };

  const loadUserProjects = async (userId: number) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/users/${userId}/projects`);
      if (!response.ok) {
        throw new Error('Error al cargar proyectos del usuario');
      }
      const data = await response.json();
      setUserProjects(data);

      // Set selected projects based on current assignments
      const assignedProjectNames = data.map((p: UserProject) => p.project_name);
      setSelectedProjects(new Set(assignedProjectNames));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectToggle = (projectName: string) => {
    const newSelected = new Set(selectedProjects);
    if (newSelected.has(projectName)) {
      newSelected.delete(projectName);
    } else {
      newSelected.add(projectName);
    }
    setSelectedProjects(newSelected);
  };

  const handleSave = async () => {
    if (!selectedUserId) {
      setError('Por favor seleccione un usuario');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`/api/users/${selectedUserId}/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_names: Array.from(selectedProjects)
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al guardar asignaciones');
      }

      setSuccess('Asignaciones de proyectos guardadas exitosamente');
      // Reload user projects to reflect changes
      await loadUserProjects(selectedUserId as number);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setSaving(false);
    }
  };

  const selectedUser = users.find(u => u.id === selectedUserId);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Asignación de Proyectos
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* User Selection */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon />
                Seleccionar Usuario
              </Typography>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Usuario Gerente</InputLabel>
                <Select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value as number)}
                  label="Usuario Gerente"
                >
                  <MenuItem value="">
                    <em>Seleccionar usuario...</em>
                  </MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.full_name} ({user.username})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedUser && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Usuario seleccionado:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedUser.full_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedUser.username} • {selectedUser.role}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Project Assignment */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon />
                Proyectos Disponibles
              </Typography>

              {!selectedUserId && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Seleccione un usuario para ver y asignar proyectos
                </Typography>
              )}

              {selectedUserId && loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              )}

              {selectedUserId && !loading && (
                <>
                  {/* Current Assignments Summary */}
                  {userProjects.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Proyectos actualmente asignados:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {userProjects.map((project) => {
                          // Find the full project data to get active status
                          const fullProject = projects.find(p => p['Nombre Proyecto'] === project.project_name);
                          const isActive = fullProject?.active !== false; // Default to true if not found

                          return (
                            <Box key={project.project_name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip
                                label={project.project_name}
                                color={isActive ? "primary" : "default"}
                                variant="outlined"
                                size="small"
                                sx={{
                                  opacity: isActive ? 1 : 0.7,
                                  color: isActive ? 'inherit' : 'text.secondary',
                                  borderColor: isActive ? 'primary.main' : 'text.secondary'
                                }}
                              />
                              {fullProject && (
                                <ProjectStatusBadge
                                  project={fullProject}
                                  size="small"
                                  showText={false}
                                  showIcon={true}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                      <Divider sx={{ mt: 2, mb: 2 }} />
                    </Box>
                  )}

                  {/* Project Selection */}
                  <Typography variant="subtitle2" gutterBottom>
                    Seleccionar proyectos para asignar:
                  </Typography>

                  <Box sx={{ maxHeight: 400, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                    {projects.map((project) => {
                      const projectName = project['Nombre Proyecto'];
                      const isActive = project.active;

                      return (
                        <FormControlLabel
                          key={projectName}
                          control={
                            <Checkbox
                              checked={selectedProjects.has(projectName)}
                              onChange={() => handleProjectToggle(projectName)}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    sx={{
                                      color: isActive ? 'inherit' : 'text.secondary',
                                      opacity: isActive ? 1 : 0.7
                                    }}
                                  >
                                    {projectName}
                                  </Typography>
                                  <ProjectStatusBadge
                                    project={project}
                                    size="small"
                                    showText={false}
                                    showIcon={true}
                                  />
                                </Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ opacity: isActive ? 1 : 0.6 }}
                                >
                                  {isActive
                                    ? "Proyecto sincronizado y actualizandose"
                                    : "Proyecto inactivo, no recibe más actualizaciones"}
                                </Typography>
                              </Box>
                            </Box>
                          }
                          sx={{
                            display: 'flex',
                            width: '100%',
                            m: 0,
                            p: 1,
                            borderRadius: 1,
                            // Ensure inactive projects remain selectable but visually distinct
                            opacity: isActive ? 1 : 0.8,
                            '&:hover': {
                              bgcolor: isActive ? 'action.hover' : 'action.selected'
                            }
                          }}
                        />
                      );
                    })}
                  </Box>

                  {/* Summary */}
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                    <Typography variant="body2" color="info.main">
                      Total de proyectos seleccionados: {selectedProjects.size}
                    </Typography>
                  </Box>

                  {/* Save Button */}
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                      onClick={handleSave}
                      disabled={saving || !selectedUserId}
                    >
                      {saving ? 'Guardando...' : 'Guardar Asignaciones'}
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ProjectAssignment;